import { supabase } from './supabaseClient';
import { resolveSharedImageUrl } from './thriftalyzeDataService';
import {
  InventoryItem,
  ItemSale,
  CreateInventoryItemRequest,
  UpdateInventoryItemRequest,
  CreateSaleRequest,
  InventoryFilters,
  InventoryStats,
  ItemMarketplace
} from '../types/inventory';

export class InventoryService {
  // Create a new inventory item
  static async createItem(request: CreateInventoryItemRequest): Promise<InventoryItem> {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    console.log('Auth check:', { user: user?.id, authError });

    if (!user) throw new Error('User not authenticated');
    if (authError) throw new Error(`Auth error: ${authError.message}`);

    console.log('Creating item for user:', user.id);

    // Upload images to Supabase Storage
    const imageUrls: string[] = [];
    for (const image of request.images) {
      const fileName = `${user.id}/${Date.now()}-${image.name}`;
      const { error } = await supabase.storage
        .from('inventory-images')
        .upload(fileName, image);

      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage
        .from('inventory-images')
        .getPublicUrl(fileName);
      imageUrls.push(publicUrl);
    }

    const { data, error } = await supabase
      .from('inventory_items')
      .insert({
        user_id: user.id,
        item_name: request.item_name,
        images: imageUrls
      })
      .select()
      .single();

    if (error) throw error;
    
    // Map database fields to interface fields
    return {
      ...data,
      images: data.images || []
    };
  }

  // Get user's inventory items
  static async getUserItems(filters?: InventoryFilters): Promise<InventoryItem[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('inventory_items')
      .select('*, item_sales(*)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters?.condition) {
      query = query.eq('condition', filters.condition);
    }
    if (filters?.min_price) {
      query = query.gte('suggested_price', filters.min_price);
    }
    if (filters?.max_price) {
      query = query.lte('suggested_price', filters.max_price);
    }
    if (filters?.date_from) {
      query = query.gte('created_at', filters.date_from);
    }
    if (filters?.date_to) {
      query = query.lte('created_at', filters.date_to);
    }

    const { data, error } = await query;
    if (error) throw error;
    
    // Map database fields to interface fields
    return Promise.all((data || []).map(async item => ({
      ...item,
      images: await Promise.all((item.images || []).map((image: string) => resolveSharedImageUrl(image)))
    })));
  }

  // Get single inventory item
  static async getItem(itemId: string): Promise<InventoryItem> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('inventory_items')
      .select('*')
      .eq('id', itemId)
      .eq('user_id', user.id)
      .single();

    if (error) throw error;
    
    // Map database fields to interface fields
    return {
      ...data,
      images: await Promise.all((data.images || []).map((image: string) => resolveSharedImageUrl(image)))
    };
  }

  // Update inventory item
  static async updateItem(itemId: string, updates: UpdateInventoryItemRequest): Promise<InventoryItem> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('inventory_items')
      .update(updates)
      .eq('id', itemId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    
    // Map database fields to interface fields
    return {
      ...data,
      images: data.images || []
    };
  }

  // Delete inventory item
  static async deleteItem(itemId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('inventory_items')
      .delete()
      .eq('id', itemId)
      .eq('user_id', user.id);

    if (error) throw error;
  }

  // Create a sale record
  static async createSale(sale: CreateSaleRequest): Promise<ItemSale> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify the item belongs to the user
    const { data: item } = await supabase
      .from('inventory_items')
      .select('id')
      .eq('id', sale.item_id)
      .eq('user_id', user.id)
      .single();

    if (!item) throw new Error('Item not found or access denied');

    const { data: existingSale, error: existingSaleError } = await supabase
      .from('item_sales')
      .select('id')
      .eq('item_id', sale.item_id)
      .maybeSingle();

    if (existingSaleError) throw existingSaleError;
    if (existingSale) throw new Error('This item has already been marked as sold');

    const { data, error } = await supabase
      .from('item_sales')
      .insert(sale)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Get sales for an item
  static async getItemSales(itemId: string): Promise<ItemSale[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('item_sales')
      .select('*')
      .eq('item_id', itemId)
      .order('sale_date', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Get inventory statistics
  static async getInventoryStats(): Promise<InventoryStats> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get all items
    const { data: items, error: itemsError } = await supabase
      .from('inventory_items')
      .select('id, suggested_price')
      .eq('user_id', user.id);

    if (itemsError) throw itemsError;

    // Get all sales
    const { data: sales, error: salesError } = await supabase
      .from('item_sales')
      .select('sale_price, item_id')
      .in('item_id', items?.map(item => item.id) || []);

    if (salesError) throw salesError;

    const totalItems = items?.length || 0;
    const totalValue = items?.reduce((sum, item) => sum + (item.suggested_price || 0), 0) || 0;
    const soldItems = sales?.length || 0;
    const soldValue = sales?.reduce((sum, sale) => sum + sale.sale_price, 0) || 0;
    const unsoldItems = totalItems - soldItems;
    const unsoldValue = totalValue - soldValue;

    return {
      total_items: totalItems,
      total_value: totalValue,
      sold_items: soldItems,
      sold_value: soldValue,
      unsold_items: unsoldItems,
      unsold_value: unsoldValue
    };
  }

  // Get marketplaces for an item
  static async getItemMarketplaces(itemId: string): Promise<ItemMarketplace[]> {
    const { data, error } = await supabase
      .from('item_marketplaces')
      .select(`
        *,
        marketplace:marketplaces(*)
      `)
      .eq('item_id', itemId);

    if (error) throw error;
    return data || [];
  }

  // Update AI analysis for an item
  static async updateAIAnalysis(itemId: string, aiAnalysis: any): Promise<InventoryItem> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('inventory_items')
      .update({
        ai_analysis: aiAnalysis,
        updated_at: new Date().toISOString()
      })
      .eq('id', itemId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    
    // Map database fields to interface fields
    return {
      ...data,
      images: data.images || []
    };
  }
}
